import { cloneAsc } from './cloneAsc'
import { createAscHostDelivery } from './ascHostDelivery'
import { ViewportState } from '../viewport'
import { knownHeaderOverlays } from './knowHeaderOverlays'
import { createOneTrustConsentRelay } from './oneTrustConsent'
import { HostConsentState } from '../consent'

declare global {
  interface Window {
    // Same type as `packages/types-workspace`, so the two declarations merge where both apply.
    dataLayer?: unknown[]
    asc_dataLayer?: Record<string, unknown>
    asc_datalayer?: Record<string, unknown>
  }
}

const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

function resolveOverlayHeight(specs: string[]): number {
  return specs.reduce((total, spec) => {
    const asNumber = parseFloat(spec.trim())
    if (!isNaN(asNumber)) return total + asNumber

    const selectors = spec
      .split(',')
      .map(s => s.trim())
      .filter(Boolean)
    for (const selector of selectors) {
      const el = document.querySelector<HTMLElement>(selector)
      if (el) return total + el.getBoundingClientRect().height
    }
    return total
  }, 0)
}

export function getHostMethods(
  element: HTMLIFrameElement,
  onViewportUpdate: (state: ViewportState) => void,
  onHostConsentUpdate: (state: HostConsentState) => void = () => {}
) {
  let isDisabled = false
  let viewportTrackingEnabled = false
  let rafId: number | null = null

  const ping = () => 'pong' as const

  const getHostUrl = (): string => {
    return window?.location?.href
  }

  const resize = (height: number): void => {
    if (isDisabled) return
    element.style.height = `${height}px`
    element.style.minHeight = `${height}px`
  }

  const disable = (): void => {
    isDisabled = true
    element.style.display = 'none'
  }

  const queryAscDataLayer = async (): Promise<Record<string, unknown> | null | undefined> => {
    const timeout = 5000
    const interval = 50
    const maxAttempts = timeout / interval

    try {
      const getAscDataLayer = (): Record<string, unknown> | null => {
        const asc_dataLayer = window.asc_dataLayer || window.asc_datalayer
        if (!asc_dataLayer) return null

        const cloned = cloneAsc(asc_dataLayer)
        return cloned
      }

      for (let i = 0; i < maxAttempts; i++) {
        const dataLayer = getAscDataLayer()
        if (dataLayer) return dataLayer
        await wait(interval)
      }
    } catch {
      return undefined
    }
  }

  const enable = (): void => {
    isDisabled = false

    if (element.style.display === 'none') {
      element.style.display = ''
    }
  }

  const forwardGTMEvent = (eventData: Record<string, unknown>): void => {
    try {
      // GTM replays pushes made before it loads, so an array created here is not lost.
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push(eventData)
    } catch {
      // ignore
    }
  }

  const sendAscEvent = createAscHostDelivery(window)

  const enableOneTrustConsent = createOneTrustConsentRelay(window, onHostConsentUpdate).enable

  const deleteIframe = (): void => {
    // this.removeIframe(iframeId)
  }

  const showModal = (iframeUrl: string): void => {
    if (isDisabled) return

    // TODO: Implement modal functionality
    console.log('showModal not implemented yet:', iframeUrl)
  }

  const parseUrl = (url: string): URL | null => {
    try {
      return new URL(url)
    } catch {
      return null
    }
  }

  const allowedHosts = ['localhost', 'hub.lectrium.com', 'staging.hub.lectrium.com', 'lab.hub.lectrium.com']
  const loadScript = (src: string): void => {
    const url = parseUrl(src)
    if (!url) return
    if (!allowedHosts.includes(url.hostname)) return
    if (document.querySelector(`script[src="${src}"]`)) return

    const script = document.createElement('script')
    script.src = src
    document.head.appendChild(script)
  }

  const scrollTo = (sectionOffsetInIframe: number, overlaySpecs: string[]): void => {
    const iframeTop = element.getBoundingClientRect().top + window.scrollY
    const overlayHeight = resolveOverlayHeight(overlaySpecs)
    window.scrollTo({ top: iframeTop + sectionOffsetInIframe - overlayHeight, behavior: 'smooth' })
  }

  const enableViewportTracking = (): void => {
    if (viewportTrackingEnabled) return

    viewportTrackingEnabled = true

    function getHeaderOverlay(): number {
      for (const selector of knownHeaderOverlays) {
        const element = document.querySelector(selector)
        if (element) return element.getBoundingClientRect().bottom
      }
      return 100
    }

    const sendUpdate = () => {
      const rect = element.getBoundingClientRect()

      onViewportUpdate({
        parentScrollY: window.scrollY,
        parentViewportHeight: window.innerHeight,
        iframeTopInPage: rect.top + window.scrollY,
        topOverlayHeight: getHeaderOverlay(),
      })
    }

    // Throttle updates using requestAnimationFrame (max 60fps)
    const scheduleUpdate = () => {
      if (rafId !== null) return
      rafId = requestAnimationFrame(() => {
        rafId = null
        sendUpdate()
      })
    }

    window.addEventListener('scroll', () => scheduleUpdate(), { passive: true })
    window.addEventListener('resize', () => scheduleUpdate(), { passive: true })

    // Send initial state
    sendUpdate()
  }

  return {
    ping,
    getHostUrl,
    resize,
    disable,
    enable,
    forwardGTMEvent,
    sendAscEvent,
    queryAscDataLayer,
    delete: deleteIframe,
    showModal,
    loadScript,
    enableViewportTracking,
    enableOneTrustConsent,
    scrollTo,
  }
}
export type HostMethods = ReturnType<typeof getHostMethods>
